import mapboxgl from 'mapbox-gl';

export const MAP_PROVIDER = (process.env.REACT_APP_MAP_PROVIDER || 'osm').toLowerCase();
export const isNeshan = MAP_PROVIDER === 'neshan';
export const NESHAN_KEY = process.env.REACT_APP_NESHAN_KEY || 'web.6779b40b22fb4f41870de5ca22934953';
export const OSM_TILE_URL = process.env.REACT_APP_OSM_TILE_URL || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

export const osmStyle = {
  version: 8,
  sources: {
    'osm-tiles': {
      type: 'raster',
      tiles: process.env.REACT_APP_OSM_TILE_URL ? [process.env.REACT_APP_OSM_TILE_URL] : [
        'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
      ],
      tileSize: 256,
      minzoom: 0,
      maxzoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }
  },
  layers: [
    {
      id: 'osm-tiles-layer',
      type: 'raster',
      source: 'osm-tiles',
      minzoom: 0,
      maxzoom: 22
    }
  ]
};

export const getMapLib = () => {
  if (isNeshan && typeof window !== 'undefined' && window.nmp_mapboxgl) {
    return window.nmp_mapboxgl;
  }
  return mapboxgl;
};

export const createMapInstance = (options = {}) => {
  const {
    container = 'myMap',
    center = [46.270212, 38.0843038],
    zoom = 5,
    minZoom,
    maxZoom,
    mapType = 'neshanVector',
    mapKey = NESHAN_KEY,
    showNavigation = true,
    navigationPosition = 'top-left',
    ...rest
  } = options;

  const containerEl = typeof container === 'string' ? document.getElementById(container) : container;

  if (isNeshan && typeof window !== 'undefined' && window.nmp_mapboxgl) {
    const neshanMapTypes = window.nmp_mapboxgl.Map && window.nmp_mapboxgl.Map.mapTypes;
    const map = new window.nmp_mapboxgl.Map({
      container: containerEl,
      mapType: (neshanMapTypes && neshanMapTypes[mapType]) || (neshanMapTypes && neshanMapTypes.neshanVector) || 'neshanVector',
      center,
      zoom,
      minZoom: minZoom !== undefined ? minZoom : 4,
      maxZoom: maxZoom !== undefined ? maxZoom : 17,
      pitch: 0,
      trackResize: true,
      mapKey,
      poi: false,
      traffic: false,
      isTouchPlatform: false,
      mapTypeControllerOptions: {
        show: true,
        position: 'bottom-left'
      },
      ...rest
    });
    if (showNavigation && window.nmp_mapboxgl.NavigationControl) {
      map.addControl(new window.nmp_mapboxgl.NavigationControl({ showCompass: true, showZoom: true }), navigationPosition);
    }
    return map;
  }

  // OpenStreetMap default (pure mapbox-gl, completely free and open-source)
  const map = new mapboxgl.Map({
    container: containerEl,
    style: osmStyle,
    center,
    zoom,
    minZoom: minZoom !== undefined ? minZoom : 2,
    maxZoom: maxZoom !== undefined ? maxZoom : 18,
    pitch: 0,
    trackResize: true,
    ...rest
  });
  if (showNavigation && mapboxgl.NavigationControl) {
    map.addControl(new mapboxgl.NavigationControl({ showCompass: true, showZoom: true }), navigationPosition);
  }
  return map;
};

export const getExternalMapUrl = (lat, lng) => {
  if (isNeshan) {
    return `https://nshn.ir/?lat=${lat}&lng=${lng}`;
  }
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=16/${lat}/${lng}`;
};

const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

export const toPersianDigits = (str) => {
  if (str === null || str === undefined) return '';
  return String(str).replace(/[0-9]/g, (w) => persianDigits[+w]);
};

export const formatPersianDuration = (val) => {
  if (val === undefined || val === null || val === '') {
    return 'نامشخص';
  }

  // Handle strings formatted as "HH:MM:SS" or "MM:SS"
  if (typeof val === 'string' && val.includes(':')) {
    const parts = val.split(':').map((p) => parseFloat(p));
    let h = 0;
    let m = 0;
    let s = 0;
    if (parts.length === 3) {
      [h, m, s] = parts;
    } else if (parts.length === 2) {
      [m, s] = parts;
    }
    const res = [];
    if (h > 0) res.push(`${toPersianDigits(Math.floor(h))} ساعت`);
    if (m > 0) res.push(`${toPersianDigits(Math.floor(m))} دقیقه`);
    if (s > 0 || res.length === 0) res.push(`${toPersianDigits(Math.round(s))} ثانیه`);
    return res.join(' و ');
  }

  const num = parseFloat(val);
  if (isNaN(num)) {
    return toPersianDigits(String(val));
  }

  const totalSeconds = Math.round(num);
  if (totalSeconds <= 0) {
    return '۰ ثانیه';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts = [];
  if (hours > 0) {
    parts.push(`${toPersianDigits(hours)} ساعت`);
  }
  if (minutes > 0) {
    parts.push(`${toPersianDigits(minutes)} دقیقه`);
  }
  if (seconds > 0 || parts.length === 0) {
    parts.push(`${toPersianDigits(seconds)} ثانیه`);
  }

  return parts.join(' و ');
};

export const formatCoordinate = (val) => {
  if (val === undefined || val === null || val === '' || isNaN(parseFloat(val))) {
    return 'نامشخص';
  }
  const num = parseFloat(val);
  return toPersianDigits(num.toFixed(6));
};

export const formatPersianDateTime = (rawDate, momentLib) => {
  if (!rawDate) return 'نامشخص';
  const m = momentLib ? momentLib(rawDate) : null;
  if (m && m.isValid()) {
    const hasSeconds = m.seconds() > 0;
    const timeFormat = hasSeconds ? 'HH:mm:ss' : 'HH:mm';
    return toPersianDigits(m.format(`jYYYY/jMM/jDD | ${timeFormat}`));
  }
  return toPersianDigits(String(rawDate));
};

export const isStopPoint = (item) => {
  if (!item) return false;
  return Boolean(
    item.is_stop === 1 ||
    item.is_stop === true ||
    item.is_stop === '1' ||
    item.is_stop === 'true' ||
    item.is_stopped === 1 ||
    item.is_stopped === true ||
    item.is_stopped === '1' ||
    item.is_stopped === 'true' ||
    (item.stop_duration !== undefined && item.stop_duration !== null && parseFloat(item.stop_duration) > 0) ||
    (item.is_stop && item.is_stop !== '0' && item.is_stop !== 'false') ||
    (item.is_stopped && item.is_stopped !== '0' && item.is_stopped !== 'false')
  );
};

export const getStopPointContent = (data, momentLib) => {
  if (!data) return '';
  const latVal = data.lat !== undefined && data.lat !== null ? data.lat : data.latitude;
  const lngVal = data.lng !== undefined && data.lng !== null ? data.lng : (data.lon !== undefined ? data.lon : data.longitude);

  const rawStartTime =
    data.stop_start_time ||
    data.stop_start ||
    data.start_time ||
    data.start_date ||
    data.start_at ||
    data.sent_at ||
    data.created_at ||
    data.time ||
    data.date;
  const formattedStartTime = formatPersianDateTime(rawStartTime || data.sent_at, momentLib);

  const rawDuration =
    data.stop_duration !== undefined && data.stop_duration !== null
      ? data.stop_duration
      : data.duration;
  const formattedDuration = formatPersianDuration(rawDuration);

  const formattedLat = formatCoordinate(latVal);
  const formattedLng = formatCoordinate(lngVal);

  return (
    '<div id="stopPointContent" class="stop-tooltip">' +
      '<div class="stop-tooltip-header">' +
        '<span class="stop-tooltip-title">نقطه توقف</span>' +
      '</div>' +
      '<div class="stop-tooltip-body">' +
        '<div class="stop-tooltip-row">' +
          '<span class="stop-tooltip-label">زمان شروع توقف:</span>' +
          '<span class="stop-tooltip-value dir-ltr">' + formattedStartTime + '</span>' +
        '</div>' +
        '<div class="stop-tooltip-row">' +
          '<span class="stop-tooltip-label">عرض جغرافیایی:</span>' +
          '<span class="stop-tooltip-value dir-ltr">' + formattedLat + '</span>' +
        '</div>' +
        '<div class="stop-tooltip-row">' +
          '<span class="stop-tooltip-label">طول جغرافیایی:</span>' +
          '<span class="stop-tooltip-value dir-ltr">' + formattedLng + '</span>' +
        '</div>' +
        '<div class="stop-tooltip-row">' +
          '<span class="stop-tooltip-label">مدت توقف:</span>' +
          '<span class="stop-tooltip-value">' + formattedDuration + '</span>' +
        '</div>' +
      '</div>' +
    '</div>'
  );
};

export const getStartPointContent = (data, momentLib) => {
  if (!data) return '';
  const isStop = isStopPoint(data);
  const latVal = data.lat !== undefined && data.lat !== null ? data.lat : data.latitude;
  const lngVal = data.lng !== undefined && data.lng !== null ? data.lng : (data.lon !== undefined ? data.lon : data.longitude);

  const rawStartTime =
    data.stop_start_time ||
    data.stop_start ||
    data.start_time ||
    data.start_date ||
    data.start_at ||
    data.sent_at ||
    data.created_at ||
    data.time ||
    data.date;
  const formattedStartTime = formatPersianDateTime(rawStartTime || data.sent_at, momentLib);

  const formattedLat = formatCoordinate(latVal);
  const formattedLng = formatCoordinate(lngVal);

  let stopDurationHtml = '';
  if (isStop) {
    const rawDuration =
      data.stop_duration !== undefined && data.stop_duration !== null
        ? data.stop_duration
        : data.duration;
    const formattedDuration = formatPersianDuration(rawDuration);
    stopDurationHtml =
      '<div class="stop-tooltip-row">' +
        '<span class="stop-tooltip-label">مدت توقف:</span>' +
        '<span class="stop-tooltip-value">' + formattedDuration + '</span>' +
      '</div>';
  }

  return (
    '<div id="stopPointContent" class="stop-tooltip">' +
      '<div class="stop-tooltip-header">' +
        '<span class="stop-tooltip-title start-point-title">نقطه شروع' +
          (isStop ? ' (توقف)' : '') +
        '</span>' +
      '</div>' +
      '<div class="stop-tooltip-body">' +
        '<div class="stop-tooltip-row">' +
          '<span class="stop-tooltip-label">زمان:</span>' +
          '<span class="stop-tooltip-value dir-ltr">' + formattedStartTime + '</span>' +
        '</div>' +
        '<div class="stop-tooltip-row">' +
          '<span class="stop-tooltip-label">عرض جغرافیایی:</span>' +
          '<span class="stop-tooltip-value dir-ltr">' + formattedLat + '</span>' +
        '</div>' +
        '<div class="stop-tooltip-row">' +
          '<span class="stop-tooltip-label">طول جغرافیایی:</span>' +
          '<span class="stop-tooltip-value dir-ltr">' + formattedLng + '</span>' +
        '</div>' +
        stopDurationHtml +
      '</div>' +
    '</div>'
  );
};

export const getDestinationPointContent = (data, momentLib) => {
  if (!data) return '';
  const isStop = isStopPoint(data);
  const latVal = data.lat !== undefined && data.lat !== null ? data.lat : data.latitude;
  const lngVal = data.lng !== undefined && data.lng !== null ? data.lng : (data.lon !== undefined ? data.lon : data.longitude);

  const rawStartTime =
    data.stop_start_time ||
    data.stop_start ||
    data.start_time ||
    data.start_date ||
    data.start_at ||
    data.sent_at ||
    data.created_at ||
    data.time ||
    data.date;
  const formattedStartTime = formatPersianDateTime(rawStartTime || data.sent_at, momentLib);

  const formattedLat = formatCoordinate(latVal);
  const formattedLng = formatCoordinate(lngVal);

  const dist = data.total_distance != null ? parseFloat(data.total_distance / 1000).toFixed(1) : 0;

  let stopDurationHtml = '';
  if (isStop) {
    const rawDuration =
      data.stop_duration !== undefined && data.stop_duration !== null
        ? data.stop_duration
        : data.duration;
    const formattedDuration = formatPersianDuration(rawDuration);
    stopDurationHtml =
      '<div class="stop-tooltip-row">' +
        '<span class="stop-tooltip-label">مدت توقف:</span>' +
        '<span class="stop-tooltip-value">' + formattedDuration + '</span>' +
      '</div>';
  }

  return (
    '<div id="stopPointContent" class="stop-tooltip">' +
      '<div class="stop-tooltip-header">' +
        '<span class="stop-tooltip-title destination-point-title">نقطه پایان' +
          (isStop ? ' (توقف)' : '') +
        '</span>' +
      '</div>' +
      '<div class="stop-tooltip-body">' +
        '<div class="stop-tooltip-row">' +
          '<span class="stop-tooltip-label">زمان:</span>' +
          '<span class="stop-tooltip-value dir-ltr">' + formattedStartTime + '</span>' +
        '</div>' +
        '<div class="stop-tooltip-row">' +
          '<span class="stop-tooltip-label">مسافت طی شده:</span>' +
          '<span class="stop-tooltip-value">' + toPersianDigits(dist) + ' کیلومتر</span>' +
        '</div>' +
        '<div class="stop-tooltip-row">' +
          '<span class="stop-tooltip-label">عرض جغرافیایی:</span>' +
          '<span class="stop-tooltip-value dir-ltr">' + formattedLat + '</span>' +
        '</div>' +
        '<div class="stop-tooltip-row">' +
          '<span class="stop-tooltip-label">طول جغرافیایی:</span>' +
          '<span class="stop-tooltip-value dir-ltr">' + formattedLng + '</span>' +
        '</div>' +
        stopDurationHtml +
      '</div>' +
    '</div>'
  );
};

export const getLocPointContent = (data, momentLib) => {
  if (!data) return '';
  const formattedTime = momentLib
    ? formatPersianDateTime(data.sent_at, momentLib)
    : toPersianDigits(data.sent_at || '');
  return (
    '<div id="locPointContent">' +
      '<p>سرعت: ' +
      toPersianDigits(data.speed || 0) +
      ' کیلو متر بر ساعت</p>' +
      '<p>' +
      formattedTime +
      '</p>' +
    '</div>'
  );
};


